import { useContext, useEffect, useRef, useState } from 'react';

import { CellClassParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { useSearchParams } from 'react-router-dom';

import { useGetDay7FrozenEmbryosQuery } from '@/app/services/excel';
import { WithLocalStorage } from '@/components/hoc';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { Day7FrozenEmbryo } from '@/rtk-query.gql';
import { PrimaryContext } from '@/services';

import { pseDay7FrozenEmbryosColDefs } from './columnDefs';

export const PseDay7FrozenEmbryosPage = () => {
  const defaultGridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const gridRef = useRef<AgGridReact>(null);

  const [params] = useSearchParams();
  const beginDate = params.get('beginDate');
  const endDate = params.get('endDate');
  const siteId = params.get('site');
  const accountId = params.get('account');

  const [rowData, setRowData] = useState<Day7FrozenEmbryo[]>([]);

  const { data: pseDay7FrozenEmbryosData } = useGetDay7FrozenEmbryosQuery({
    beginDate,
    endDate,
    siteId,
    accountId,
  });

  useEffect(() => {
    if (pseDay7FrozenEmbryosData) {
      setRowData(pseDay7FrozenEmbryosData);
    }
  }, [pseDay7FrozenEmbryosData]);

  const handleShadeRows = () => {
    if (!gridRef.current?.api) return;

    const cellClasses = new Map<string, string>();
    let currentColor = 0;
    let previousPlate = '';

    gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
      const currentPlate = node?.data?.plateNumber;

      if (previousPlate && currentPlate !== previousPlate) {
        currentColor = 1 - currentColor;
      }

      cellClasses.set(
        node.id as string,
        currentColor ? 'bg-tog-light-green' : 'bg-tog-light-purple',
      );
      previousPlate = currentPlate as string;
    });

    const updatedColumns = gridRef.current.api.getColumns()?.map((col) => {
      const colDef = col.getColDef();

      return {
        ...colDef,
        cellClassRules: {
          ...(colDef.cellClassRules || {}),
          'bg-tog-light-purple': (params: CellClassParams) =>
            cellClasses.get(params.node.id as string) === 'bg-tog-light-purple',
          'bg-tog-light-green': (params: CellClassParams) =>
            cellClasses.get(params.node.id as string) === 'bg-tog-light-green',
        },
      };
    });

    if (updatedColumns) {
      gridRef.current.api.setGridOption('columnDefs', updatedColumns);
    }

    gridRef.current.api.refreshCells();
  };

  return (
    <div className="pseDay7__grid">
      <Button label="Shade" onClick={handleShadeRows} className="m-1" />
      <WithLocalStorage storageKey="pseDay7-grid">
        <AgGridReact
          gridOptions={defaultGridOptions}
          className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}`}
          defaultColDef={defaultColDef}
          columnDefs={pseDay7FrozenEmbryosColDefs}
          rowData={rowData}
          ref={gridRef}
          onCellValueChanged={(params) => {
            const updatedData = rowData.map((row) =>
              row === params.oldValue
                ? { ...row, [params.column.getId()]: params.newValue }
                : row,
            );
            setRowData(updatedData);
          }}
        />
      </WithLocalStorage>
    </div>
  );
};
